import { useCallback, useEffect, useState } from "react";

import {
  contextoCompleto,
  gravarContexto,
  repararContexto,
} from "../utils/contextoOrcamento";

/**
 * 🧭 Garante que coligada/filial/ano existam ANTES de qualquer tela renderizar.
 *
 * Até aqui, a Home era a única escritora dessas chaves — e escrevia dentro de
 * `if`s. Bastava a `sql_MAN_ORC_0002` falhar, a lista de filiais atrasar, ou o
 * usuário entrar direto numa tela de lançamento (o `Login.jsx` faz
 * `localStorage.clear()`, então tudo começa vazio) para as consultas saírem com
 * `CODCOLIGADA = 'null'` e voltarem vazias, sem erro nenhum.
 *
 * Usado pelo Layout, que é pai de todas as rotas privadas.
 *
 * Estados: "carregando" | "pronto" | "erro".
 */
export default function useContextoOrcamento() {
  // Conserto síncrono primeiro: se `selectedFilial` existe, dá para derivar as
  // chaves que faltam sem rede nenhuma e já começar pronto.
  const [estado, setEstado] = useState(() =>
    repararContexto() ? "pronto" : "carregando"
  );
  const [nome, setNome] = useState(
    () => localStorage.getItem("perfilNome") || ""
  );
  const [tentativa, setTentativa] = useState(0);

  const tentarNovamente = useCallback(() => {
    setEstado("carregando");
    setTentativa((n) => n + 1);
  }, []);

  useEffect(() => {
    // Já resolvido no conserto síncrono (ou numa tentativa anterior).
    if (estado === "pronto" && nome) return;
    if (estado === "erro") return;

    const token = localStorage.getItem("authToken");
    if (!token) {
      // Sem token o PrivateRoute já redireciona; não é caso de erro de contexto.
      setEstado("erro");
      return;
    }

    const controller = new AbortController();
    // Com SEM_IMAGEM a resposta é de ~1 KB e volta em milissegundos. Os 60s
    // ficam como folga larga para link ruim — antes eram 15s contra uma
    // resposta de 3 MB, e era isso que cancelava a chamada no meio e mostrava
    // "Contexto não definido" com a consulta funcionando.
    const expirou = setTimeout(() => controller.abort(), 60000);

    (async () => {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const codusuario = payload.username || payload.id;
        if (!codusuario) throw new Error("token sem usuário");

        const resposta = await fetch(
          `${process.env.REACT_APP_API_URL}/totvs-sql/sql_MAN_ORC_0002/filter`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              WHERE: `AND CODUSUARIO = '${codusuario}'  `,
              ORDER_BY: "CODUSUARIO",
              // Coligada/filial NÃO podem depender da foto: com SEM_IMAGEM a
              // resposta cai de ~1 MB para ~1 KB. Quem precisa do avatar é a
              // Home, e ela pede a versão completa por conta própria.
              SEM_IMAGEM: true,
            }),
            signal: controller.signal,
          }
        );

        const resultado = await resposta.json();
        const usuario = resultado?.data?.[0];
        if (!usuario) throw new Error("usuário sem cadastro de contexto");

        if (usuario.USUARIO) {
          localStorage.setItem("perfilNome", usuario.USUARIO);
          setNome(usuario.USUARIO);
        }

        // Não sobrescreve um contexto que o usuário já escolheu no modal.
        if (!contextoCompleto()) {
          gravarContexto({
            coligada: usuario.CODCOLIGADA,
            filial: usuario.CODFILIAL,
            nomeFilial: usuario.FILIAL,
          });
        }

        setEstado(contextoCompleto() ? "pronto" : "erro");
      } catch (erro) {
        if (erro.name === "AbortError") {
          setEstado(contextoCompleto() ? "pronto" : "erro");
          return;
        }
        console.error("❌ Não foi possível resolver o contexto:", erro);
        // Se o contexto já estava completo, a falha só custou o nome — seguir
        // em frente é melhor do que travar quem consegue trabalhar.
        setEstado(contextoCompleto() ? "pronto" : "erro");
      } finally {
        clearTimeout(expirou);
      }
    })();

    return () => {
      clearTimeout(expirou);
      controller.abort();
    };
    // Só `tentativa`: `estado` e `nome` são lidos aqui, mas entrariam em laço,
    // já que o próprio efeito os altera. O botão "Tentar novamente" incrementa
    // `tentativa` e é isso que redispara a busca.
  }, [tentativa]);

  return { estado, nome, tentarNovamente };
}
